import { statuses as couponStatuses } from '../../constants/subscription/coupon';
import { LoaderError, TransportError } from '../errors';
import type { LoaderContext } from '../types';
import {
  type CouponDiscountableProduct,
  type CouponLogger,
  decorateProductsWithCoupons,
} from './couponDecorators';

export interface SubscriptionProductInput {
  skus: string[];
  countryCode?: string;
  isStripe?: boolean;
  disableCache?: boolean;
  disableCouponCache?: boolean;
  meta?: {
    isRenew?: boolean;
    defaultSkus?: string[];
    fromQuery?: string[];
  };
  coupon?: {
    state?: Record<string, unknown> | null;
    allowDefault?: boolean;
  };
}

const DEFAULT_ENDPOINT = 'subscription.getProductsBySkus';

function ensureSkusValid(skus: unknown): string[] {
  if (!Array.isArray(skus) || skus.length === 0) {
    throw new LoaderError({
      type: 'Validation',
      status: 400,
      endpoint: DEFAULT_ENDPOINT,
      retryable: false,
      cause: new Error('Expected at least one SKU.'),
    });
  }

  return skus;
}

export async function getProductsBySkus<TProduct extends CouponDiscountableProduct>(
  ctx: LoaderContext,
  input: SubscriptionProductInput,
): Promise<TProduct[]> {
  const skus = ensureSkusValid(input.skus);
  const { countryCode, isStripe, disableCache, disableCouponCache } = input;
  const params = {
    skus,
    skusList: skus,
    countryCode,
    noCache: disableCache,
    noCouponCache: disableCouponCache,
    isStripe,
  };

  try {
    const transportResponse = await ctx.transport.getProductsBySkus(params);
    const allowDefaultCoupons = input.coupon?.allowDefault ?? true;
    const fetchDefaultCoupon = async (product: Record<string, unknown>) => {
      if (!product?.defaultCouponCode) {
        return null;
      }

      const response = await ctx.transport.lookupCoupon({
        couponCode: product.defaultCouponCode,
        status: couponStatuses.redeemable,
        isStripe: input.isStripe,
      });

      return response.data ?? null;
    };

    const decoratedProducts = await decorateProductsWithCoupons<TProduct>({
      products: transportResponse.data,
      couponFromState: input.coupon?.state,
      fetchDefaultCoupon: allowDefaultCoupons ? fetchDefaultCoupon : null,
      logger: ctx.logger as CouponLogger | undefined,
    });

    return decoratedProducts;
  } catch (error) {
    if (error instanceof TransportError) {
      throw new LoaderError({
        ...error.details,
        endpoint: error.details.endpoint ?? DEFAULT_ENDPOINT,
        cause: error,
      });
    }

    throw new LoaderError({
      type: 'Unknown',
      status: 500,
      retryable: false,
      endpoint: DEFAULT_ENDPOINT,
      cause: error,
    });
  }
}
